import { useEffect, useState } from "react";
import { fetchBrokerages } from "../api";
import RankingTable from "../components/RankingTable";

export default function SearchByName({ search, setSearch, buildingType }) {
  const [brokerages, setBrokerages] = useState([]);
  const [searched, setSearched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchModeActive, setSearchModeActive] = useState(false);

  const dedupeById = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  };

  const loadPage = async (pageIndex) => {
    if (loadingMore || !hasMore) return; // prevent duplicate calls
    setLoadingMore(true);
    try {
      const data = await fetchBrokerages({
        limit: 50,
        offset: pageIndex * 50,
        buildingType: buildingType === "All" ? null : buildingType,
      });
      setBrokerages((prev) => dedupeById([...prev, ...data]));
      setHasMore(data.length === 50);
      setPage(pageIndex + 1);
    } finally {
      setLoadingMore(false);
    }
  };

  // Effect: trigger on buildingType change
  useEffect(() => {
    setBrokerages([]);
    setSearched(null);
    setPage(0);
    setHasMore(true);

    const fetchData = async () => {
      setLoading(true);
      try {
        if (!search.trim()) {
          // NO search term: fetch full list for selected buildingType
          const data = await fetchBrokerages({
            limit: 50,
            offset: 0,
            buildingType: buildingType === "All" ? null : buildingType,
          });
          setBrokerages(data);
          setHasMore(data.length === 50);
          setSearchModeActive(false);
          setPage(1);
        } else {
          // Search term exists: run search logic
          setSearchModeActive(true);
          const result = await fetchBrokerages({
            search,
            buildingType: buildingType === "All" ? null : buildingType,
          });
          const found = result[0] || null;
          setSearched(found);
          if (found) {
            const startRank = Math.max(1, found.rank - 10);
            const endRank = found.rank + 10;
            const neighbors = await fetchBrokerages({
              rankStart: startRank,
              rankEnd: endRank,
              buildingType: buildingType === "All" ? null : buildingType,
            });
            setBrokerages(dedupeById(neighbors));
            setHasMore(false); // disable infinite scroll when in search mode
          } else {
            setBrokerages([]);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [buildingType]);

  // Infinite scroll listener
  useEffect(() => {
    if (searchModeActive) return; // disable infinite scroll during search mode

    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.offsetHeight - 200;

      if (scrollPosition >= threshold && !loadingMore && hasMore) {
        loadPage(page);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, loadingMore, hasMore, searchModeActive]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) {
      setLoading(true);
      setSearchModeActive(false);
      try {
        const data = await fetchBrokerages({
          limit: 50,
          offset: 0,
          buildingType: buildingType === "All" ? null : buildingType,
        });
        setBrokerages(data);
        setHasMore(data.length === 50);
        setPage(1);
        setSearched(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setSearchModeActive(true);
    try {
      const result = await fetchBrokerages({
        search,
        buildingType: buildingType === "All" ? null : buildingType,
      });
      const found = result[0] || null;
      setSearched(found);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearch("");
    setSearched(null);
    setSearchModeActive(false);
    setPage(0);
    setHasMore(true);
    setBrokerages([]);
    (async () => {
      setLoading(true);
      try {
        const data = await fetchBrokerages({
          limit: 50,
          offset: 0,
          buildingType: buildingType === "All" ? null : buildingType,
        });
        setBrokerages(data);
        setHasMore(data.length === 50);
        setPage(1);
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <>
      <form onSubmit={handleSearch} className="flex gap-2 mb-6 justify-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brokerage by name"
          className="border border-gray-300 rounded px-3 py-2 w-80 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white placeholder-gray-400 text-gray-900"
        />
        <button
          type="submit"
          className="bg-white text-blue-600 px-4 py-2 rounded hover:bg-gray-200 transition font-semibold border border-blue-600"
        >
          Search
        </button>
        {searchModeActive && (
          <button
            type="button"
            onClick={clearSearch}
            className="bg-white text-gray-800 px-4 py-2 rounded hover:bg-gray-200 transition font-semibold border border-gray-400"
          >
            Reset
          </button>
        )}
      </form>

      {searched && searchModeActive && (
        <div
          className="mb-6 bg-blue-50 border border-blue-200 rounded p-4 text-center cursor-pointer hover:bg-blue-100 transition"
          onClick={() => window.location.assign(`/brokerage/${searched.id}`)}
        >
          <b className="text-lg text-blue-700">{searched.name}</b>{" "}
          <span className="text-gray-700">
            (Rank: {searched.rank}, Score: {Math.round(searched.score)})
          </span>
        </div>
      )}

      {!loading && searchModeActive && searched === null && (
        <div className="text-center text-gray-200 font-semibold mb-6">
          No brokerage found matching that name.
        </div>
      )}

      {loading && brokerages.length === 0 ? (
        <div className="flex justify-center py-10">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-10 w-10 mr-2 animate-spin"></div>
          <span className="text-white ml-2">Loading rankings...</span>
        </div>
      ) : (
        <RankingTable
          brokerages={brokerages}
          highlightId={searched ? searched.id : null}
        />
      )}

      {loadingMore && !searchModeActive && (
        <div className="flex justify-center py-4 text-white font-semibold">
          Loading more...
        </div>
      )}
    </>
  );
}
